<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import customEmailDomainAPI from 'dashboard/api/customEmailDomain';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const isFetching = ref(true);
const isSubmitting = ref(false);
const isVerifying = ref(false);
const isRemoving = ref(false);
const domainInput = ref('');
const sendingDomain = ref(null);

const isVerified = computed(() => sendingDomain.value?.status === 'verified');

const dkimRecords = computed(() => {
  if (!sendingDomain.value) return [];
  return (sendingDomain.value.dkim_tokens || []).map(token => ({
    host: `${token}._domainkey.${sendingDomain.value.domain}`,
    value: `${token}.dkim.amazonses.com`,
  }));
});

const dmarcRecord = computed(() => {
  if (!sendingDomain.value) return null;
  const { domain } = sendingDomain.value;
  return {
    host: `_dmarc.${domain}`,
    value: `v=DMARC1; p=none; rua=mailto:dmarc@${domain}`,
  };
});

async function fetchDomain() {
  isFetching.value = true;
  try {
    const { data } = await customEmailDomainAPI.get();
    sendingDomain.value = data?.id ? data : null;
  } catch (error) {
    sendingDomain.value = null;
  } finally {
    isFetching.value = false;
  }
}

async function registerDomain() {
  if (!domainInput.value.trim()) return;
  isSubmitting.value = true;
  try {
    const { data } = await customEmailDomainAPI.register(
      domainInput.value.trim()
    );
    sendingDomain.value = data;
    domainInput.value = '';
    useAlert(t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.REGISTER.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.REGISTER.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
}

async function verifyDomain() {
  isVerifying.value = true;
  try {
    const { data } = await customEmailDomainAPI.verify();
    sendingDomain.value = data;
    useAlert(
      data.status === 'verified'
        ? t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.VERIFY.SUCCESS')
        : t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.VERIFY.PENDING')
    );
  } catch (error) {
    useAlert(t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.VERIFY.ERROR'));
  } finally {
    isVerifying.value = false;
  }
}

async function removeDomain() {
  isRemoving.value = true;
  try {
    await customEmailDomainAPI.remove();
    sendingDomain.value = null;
    useAlert(t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.REMOVE.SUCCESS'));
  } catch (error) {
    useAlert(t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.REMOVE.ERROR'));
  } finally {
    isRemoving.value = false;
  }
}

onMounted(fetchDomain);
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.TITLE')" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.SECTION.TITLE')"
        :description="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.SECTION.NOTE')"
        class="!pt-0"
      >
        <div v-if="isFetching" class="text-n-slate-11 text-sm">
          {{ t('CUSTOM_EMAIL_DOMAIN_SETTINGS.LOADING') }}
        </div>

        <form
          v-else-if="!sendingDomain"
          class="grid gap-4"
          @submit.prevent="registerDomain"
        >
          <WithLabel
            name="custom-email-domain"
            :label="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.DOMAIN.LABEL')"
          >
            <NextInput
              v-model="domainInput"
              type="text"
              class="w-full"
              :placeholder="
                t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.DOMAIN.PLACEHOLDER')
              "
            />
          </WithLabel>
          <NextButton
            type="submit"
            class="w-fit"
            :is-loading="isSubmitting"
            :label="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.REGISTER.BUTTON')"
          />
        </form>

        <div v-else class="grid gap-4">
          <div class="flex items-center gap-2">
            <span class="text-n-slate-12 font-medium">{{
              sendingDomain.domain
            }}</span>
            <span
              class="text-xs uppercase rounded-lg px-2 py-0.5 leading-none"
              :class="
                isVerified
                  ? 'text-n-teal-11 border border-n-teal-8'
                  : 'text-n-amber-11 border border-n-amber-8'
              "
            >
              {{
                isVerified
                  ? t('CUSTOM_EMAIL_DOMAIN_SETTINGS.STATUS.VERIFIED')
                  : t('CUSTOM_EMAIL_DOMAIN_SETTINGS.STATUS.PENDING')
              }}
            </span>
          </div>

          <div v-if="!isVerified" class="grid gap-2">
            <p class="text-n-slate-11 text-sm">
              {{ t('CUSTOM_EMAIL_DOMAIN_SETTINGS.DNS_INSTRUCTIONS') }}
            </p>
            <div
              v-for="record in dkimRecords"
              :key="record.host"
              class="grid gap-1 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak p-3"
            >
              <span class="text-xs text-n-slate-10">{{
                t('CUSTOM_EMAIL_DOMAIN_SETTINGS.DNS_RECORD.HOST')
              }}</span>
              <woot-code :script="record.host" />
              <span class="text-xs text-n-slate-10 mt-2">{{
                t('CUSTOM_EMAIL_DOMAIN_SETTINGS.DNS_RECORD.VALUE')
              }}</span>
              <woot-code :script="record.value" />
            </div>
          </div>

          <div class="grid gap-2">
            <p class="text-n-slate-11 text-sm">
              {{ t('CUSTOM_EMAIL_DOMAIN_SETTINGS.DMARC_RECOMMENDATION') }}
            </p>
            <div
              class="grid gap-1 rounded-lg bg-n-solid-1 outline outline-1 outline-dashed outline-n-weak p-3"
            >
              <span class="text-xs text-n-slate-10">{{
                t('CUSTOM_EMAIL_DOMAIN_SETTINGS.DNS_RECORD.HOST')
              }}</span>
              <woot-code :script="dmarcRecord.host" />
              <span class="text-xs text-n-slate-10 mt-2">{{
                t('CUSTOM_EMAIL_DOMAIN_SETTINGS.DNS_RECORD.VALUE')
              }}</span>
              <woot-code :script="dmarcRecord.value" />
            </div>
          </div>

          <div class="flex gap-2">
            <NextButton
              v-if="!isVerified"
              :is-loading="isVerifying"
              :label="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.VERIFY.BUTTON')"
              @click="verifyDomain"
            />
            <NextButton
              variant="faded"
              color="ruby"
              :is-loading="isRemoving"
              :label="t('CUSTOM_EMAIL_DOMAIN_SETTINGS.FORM.REMOVE.BUTTON')"
              @click="removeDomain"
            />
          </div>
        </div>
      </SectionLayout>
    </div>
  </div>
</template>
